import { capitalizeFirstLetterOfString } from "./stringUtils";

/* ====== Global helper functions, mostly to format dates and similar general stuff ====== */

import {
  FileOutlined, FilePdfOutlined, FileWordOutlined, FileExcelOutlined,
  FileTextOutlined, FileImageOutlined, FilePptOutlined
} from "@ant-design/icons"
import dayjs from "./dayjsConfig";

export const getUserTimezone = () => {
  return localStorage.getItem("userTimezone") || "UTC";
};

// Helper to transform a timestamp and the current time to dates in the user's timezone
export const transformTimestampToDateInTimezone = (timestamp) => {
  const userTimezone = getUserTimezone(); // Get time zone from user settings

  // Convert timestamp and current datetime to the user's timezone
  const transformedTimestamp = dayjs.utc(timestamp).tz(userTimezone);
  const transformedCurrentDateTime = dayjs.utc().tz(userTimezone);

  return {
    timestamp: transformedTimestamp,
    current: transformedCurrentDateTime
  };
};

/**
 * Transform a Day.js date (or ISO string) into a specific timezone
 * If no timezone is provided, falls back to the user's stored timezone
 *
 * @param {import('dayjs').Dayjs | string | Date} date
 * @param {string} [timezone]
 * @returns {import('dayjs').Dayjs} A Day.js object in the specified timezone
 */
export const transformDateToTimezone = (date, timezone) => {
  const tz = timezone || getUserTimezone();
  const dayjsDate = dayjs.isDayjs(date) ? date : dayjs.utc(date);
  return dayjsDate.tz(tz);
};

// Helper to convert time in seconds to DD:HH:mm:ss timestamp
export const transformSecondsToTimestamp = (timeInSeconds) => {
  // All variables use padStart to make the numbers two characters long, a leading 0 is added when needed
  const days = Math.floor(timeInSeconds / 86400); // 86400 = 60 * 60 * 24
  const hours = Math.floor((timeInSeconds % 86400) / 3600).toString().padStart(2, "0"); // 3600 = 60 * 60
  const minutes = Math.floor((timeInSeconds % 3600) / 60).toString().padStart(2, "0");
  const seconds = (timeInSeconds % 60).toString().padStart(2, "0");
  return `${days}:${hours}:${minutes}:${seconds}`;
};

/**
 * Convert any date input to a start of day UTC timestamp
 * Strings are parsed in UTC context so the stored calendar date is preserved
 * Day.js objects are converted to UTC before conversion
 *
 * @param {import('dayjs').Dayjs | string | Date} date
 * @returns {string | null} e.g. '2025-07-28T00:00:00.000Z'
 */
export const transformDateToStartOfDayTimestamp = (date, format = null) => {
  const parsedDate = typeof date === "string" ? dayjs.utc(date) : (dayjs.isDayjs(date) ? date : dayjs(date));
  if (!parsedDate.isValid()) return null;
  const result = parsedDate.utc().startOf("day");
  return format ? result.format(format) : result.toISOString();
};

/**
 * Convert any date input to an end of day UTC timestamp
 * Strings are parsed in UTC context so the calendar date is preserved
 * Day.js objects are converted to UTC before conversion
 *
 * @param {import('dayjs').Dayjs | string | Date} input
 * @returns {string | null} e.g. "2025-07-28T23:59:59.999Z"
 */
export const transformDateToEndOfDayTimestamp = (input, format = null) => {
  const parsedDate = typeof input === "string" ? dayjs.utc(input) : (dayjs.isDayjs(input) ? input : dayjs(input));
  if (!parsedDate.isValid()) return null;
  const result = parsedDate.utc().endOf("day");
  return format ? result.format(format) : result.toISOString();
};

// Helper to obtain the relative time from any timestamp
export const getRelativeTime = (timestamp = null, removeSuffix = false) => {
  if (!timestamp) return false;

  const transformedTimes = transformTimestampToDateInTimezone(timestamp);

  // Compare the time elapsed from the timestamp to the current datetime
  const comparedTimestamp = transformedTimes.timestamp.from(transformedTimes.current, removeSuffix);

  // Format it to make the first character upper case
  const formattedComparedTimestamp = comparedTimestamp?.charAt(0)?.toUpperCase() + comparedTimestamp?.slice(1);
  return formattedComparedTimestamp;
};

// Helper to obtain the duration from x date to y date as relative time
export const getDurationAsRelativeTime = (startDate = null, endDate = null, removeSuffix = true) => {
  if (!startDate || !endDate) return false;

  // Transformation still for the translation wrapper to work
  const transformedStartDate = transformTimestampToDateInTimezone(startDate);
  const transformedEndDate = transformTimestampToDateInTimezone(endDate);

  // Compare the time that would pass from start to end
  const comparedTimestamp = transformedEndDate.timestamp.from(transformedStartDate.timestamp, removeSuffix);

  // Format it to make the first character upper case
  const formattedComparedTimestamp = comparedTimestamp?.charAt(0)?.toUpperCase() + comparedTimestamp?.slice(1);
  return formattedComparedTimestamp;
};

export const formatDate = (date, timezone = null, format = "DD/MMM/YYYY") => {
  if (!date) return "--";

  const formatted = timezone
    ? transformDateToTimezone(date, timezone).format(format)
    : dayjs.utc(date).format(format);

  return capitalizeFirstLetterOfString(formatted);
};

export const parseDate = (str) => {
  if (!str || str === "-") return null;
  const date = new Date(str);
  return isNaN(date.getTime()) ? null : date;
};

// Returns the file icon depending on the extension of the document
export const getIconForFileType = (type, size = 18) => {
  const baseStyle = {
    display: "block",
    fontSize: size,
    lineHeight: 1.5,
  };

  switch (type?.toLowerCase()) {
    case "image":
      return <FileImageOutlined style={{ ...baseStyle, color: "#e67e22" }} />;
    case "pdf":
      return <FilePdfOutlined style={{ ...baseStyle, color: "#e74c3c" }} />;
    case "docx":
      return <FileWordOutlined style={{ ...baseStyle, color: "#3498db" }} />;
    case "xlsx":
      return <FileExcelOutlined style={{ ...baseStyle, color: "#2ecc71" }} />;
    case "pptx":
      return <FilePptOutlined style={{ ...baseStyle, color: "#de4f3a" }} />;
    case "txt":
      return <FileTextOutlined style={{ ...baseStyle, color: "#f1c40f" }} />;
    default:
      return <FileOutlined style={{ ...baseStyle, color: "#95a5a6" }} />;
  }
};

// Reads type of file and calls function to get file icon with the extension
export const renderFileIcon = (type, size = 18) => {
  if (!type) return false;

  let extension = "";

  if (type) {
    switch (true) {
      case type.startsWith("image/") || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(type):
        extension = "image";
        break;
      case type === "application/pdf" || /\.pdf$/i.test(type):
        extension = "pdf";
        break;
      case type === "application/msword"
        || type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        || /\.(doc|docx)$/i.test(type):
        extension = "docx";
        break;
      case type === "application/vnd.ms-excel"
        || type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        || /\.(xls|xlsx)$/i.test(type):
        extension = "xlsx";
        break;
      case type === "application/vnd.ms-powerpoint"
        || type === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        || /\.(ppt|pptx)$/i.test(type):
        extension = "pptx"
        break;
      default:
        extension = type.split("/")[1] || "txt";
        break;
    }

    return getIconForFileType(extension, size);
  }
};

export const mapStatus = (status) => {
  const STATUS = {
    "late": "Late",
    "completed late": "Completed Late",
    "completed": "Completed",
    "working on": "Working On",
    "scheduled": "Scheduled"
  };

  return STATUS[status?.toLowerCase()] || "Unknown";
};

// Function to find a specific node of a data tree using its key
export const findNodeByKey = (key, nodes = []) => {
  for (const node of nodes) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findNodeByKey(key, node.children);
      if (found) return found;
    }
  }
  return null;
};

// Helper function for folder color styling based on level
export const getFolderColor = (level) => {
  const colors = {
    1: "#00796B",
    2: "#AFB42B",
    3: "#E64A19",
    4: "#5D4037"
  };
  return colors[level] || "#000000"; // fallback for levels > 4
};

/**
 * naturalCompare(a, b)
 *
 * Splits each input string into numeric and non-numeric segments,
 * then compares segment-by-segment:
 *   - If both segments are purely digits, compare them as numbers.
 *   - Otherwise, compare them as case-insensitive strings.
 * Falls back to segment count if all segments match.
 *
 * Handles:
 *   • Numeric-only tags:      "2"  < "10"
 *   • Decimal-style tags:     "3"  < "3.1"  < "3.10"
 *   • Alphanumeric tags:      "A.1" < "A.2" < "B-1" < "B-10"
 *   • Mixed dash/dot + letters
 *
 * @param {string} a – first tag to compare
 * @param {string} b – second tag to compare
 * @returns {number} negative if a < b, positive if a > b, zero if equal
 */
export const naturalCompare = (a, b) => {
  // RegExp to capture runs of digits (\d+) or runs of non-digits (\D+)
  const segmentRE = /(\d+)|(\D+)/g;

  // Split each string into an array of segments
  const segA = a.match(segmentRE) || [];
  const segB = b.match(segmentRE) || [];

  // Compare corresponding segments
  const len = Math.min(segA.length, segB.length);
  for (let i = 0; i < len; i++) {
    const sA = segA[i];
    const sB = segB[i];

    // If identical text, move on to next segment
    if (sA === sB) continue;

    // Try parsing both as integers
    const nA = parseInt(sA, 10);
    const nB = parseInt(sB, 10);
    const isNumA = !isNaN(nA) && String(nA) === sA;
    const isNumB = !isNaN(nB) && String(nB) === sB;

    // If both segments are valid whole numbers, compare numerically
    if (isNumA && isNumB) {
      return nA - nB;
    }

    // Otherwise compare as case-insensitive strings (ignoring locale nuances)
    return sA.localeCompare(sB, undefined, { sensitivity: "base" });
  }

  // All shared segments matched; shorter array wins (e.g. "3" < "3.1")
  return segA.length - segB.length;
};

export const getFrameworkLetters = (name) => {
  if (!name) return "";
  const trimmed = name.trim();
  if (trimmed.startsWith("[")) {
    const match = trimmed.match(/^\[([^\]]+)\]/);
    return match ? match[1].toUpperCase() : trimmed.substring(0, 3).toUpperCase();
  }
  return trimmed.substring(0, 3).toUpperCase();
};

/**
 * Take the first 3 alphanumeric characters of a string (upper-cased),
 * ignoring punctuation, spaces, brackets, etc.
 *
 * @param {string} name
 * @returns {string}
 */
export const getAlphaNumFrameworkCode = (name) => {
  return name
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, 3);
};

// Resolves the first non-null field from an object based on a list of field names
export function resolveId(obj, ...fields) {
  for (const f of fields) {
    if (obj?.[f] != null) return String(obj[f]);
  }
  return null;
};